import { useState } from 'react';
import type { ProductDetail } from '../../../mocks/productDetails';

interface ProductInfoProps {
  product: ProductDetail;
}

export default function ProductInfo({ product }: ProductInfoProps) {
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [selectedColor, setSelectedColor] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);

  const handleAddToCart = () => {
    if (!selectedSize) {
      alert('Please select a size');
      return;
    }
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  const decreaseQuantity = () => {
    if (quantity > 1) setQuantity(quantity - 1);
  };

  const increaseQuantity = () => {
    if (quantity < 10) setQuantity(quantity + 1);
  };

  return (
    <div className="space-y-6">
      {/* Product Title & Price */}
      <div>
        <div className="flex items-start justify-between mb-2">
          <h1 className="text-4xl font-bold text-white tracking-tight">{product.name}</h1>
          {product.inStock && (
            <span className="bg-white/10 text-white px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap">
              In Stock
            </span>
          )}
        </div>
        <p className="text-3xl font-bold text-white">${product.price}</p>
        <p className="text-sm text-white/60 mt-1">SKU: {product.sku}</p>
      </div>

      {/* Description */}
      <p className="text-white/80 leading-relaxed">{product.description}</p>

      {/* Color Selection */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-medium text-white uppercase tracking-wider">
            Color: {product.colors[selectedColor].name}
          </label>
        </div>
        <div className="flex gap-3">
          {product.colors.map((color, index) => (
            <button
              key={index}
              onClick={() => setSelectedColor(index)}
              className={`w-12 h-12 rounded-lg border-2 transition-all duration-200 ${
                selectedColor === index
                  ? 'border-white scale-110'
                  : 'border-white/30 hover:border-white/60'
              }`}
              style={{ backgroundColor: color.hex }}
              title={color.name}
            />
          ))}
        </div>
      </div>

      {/* Size Selection */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-medium text-white uppercase tracking-wider">
            Select Size
          </label>
          <button
            onClick={() => setShowSizeGuide(!showSizeGuide)}
            className="text-sm text-white/60 hover:text-white transition-colors underline"
          >
            Size Guide
          </button>
        </div>
        <div className="grid grid-cols-5 gap-2">
          {product.sizes.map((size) => (
            <button
              key={size}
              onClick={() => setSelectedSize(size)}
              className={`py-3 px-4 rounded-lg border-2 font-medium transition-all duration-200 whitespace-nowrap ${
                selectedSize === size
                  ? 'bg-white text-black border-white'
                  : 'bg-transparent text-white border-white/30 hover:border-white'
              }`}
            >
              {size}
            </button>
          ))}
        </div>
        {showSizeGuide && (
          <div className="mt-4 p-4 bg-white/5 rounded-lg border border-white/10">
            <p className="text-sm text-white/80">
              <strong className="text-white">Size Guide:</strong> {product.fit}
            </p>
          </div>
        )}
      </div>

      {/* Quantity Selection */}
      <div>
        <label className="text-sm font-medium text-white uppercase tracking-wider block mb-3">
          Quantity
        </label>
        <div className="flex items-center gap-4">
          <div className="flex items-center bg-white/5 rounded-lg border border-white/20">
            <button
              onClick={decreaseQuantity}
              className="w-12 h-12 flex items-center justify-center text-white hover:bg-white/10 transition-colors"
            >
              <i className="ri-subtract-line text-xl"></i>
            </button>
            <span className="w-16 text-center text-white font-medium">{quantity}</span>
            <button
              onClick={increaseQuantity}
              className="w-12 h-12 flex items-center justify-center text-white hover:bg-white/10 transition-colors"
            >
              <i className="ri-add-line text-xl"></i>
            </button>
          </div>
          <span className="text-sm text-white/60">Max 10 per order</span>
        </div>
      </div>

      {/* Add to Cart Button */}
      <button
        onClick={handleAddToCart}
        className={`w-full py-4 rounded-lg font-bold text-lg uppercase tracking-wider transition-all duration-200 whitespace-nowrap ${
          addedToCart
            ? 'bg-green-500 text-white'
            : 'bg-white text-black hover:bg-white/90'
        }`}
      >
        {addedToCart ? (
          <span className="flex items-center justify-center gap-2">
            <i className="ri-check-line text-2xl"></i>
            Added to Cart
          </span>
        ) : (
          <span className="flex items-center justify-center gap-2">
            <i className="ri-shopping-bag-line text-xl"></i>
            Add to Cart
          </span>
        )}
      </button>

      {/* Product Highlights */}
      <div className="pt-6 border-t border-white/10">
        <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4">
          Why It Stands Out
        </h3>
        <ul className="space-y-2">
          {product.highlights.map((highlight, index) => (
            <li key={index} className="flex items-start gap-3 text-white/80 text-sm">
              <i className="ri-check-line text-white text-lg mt-0.5 flex-shrink-0"></i>
              <span>{highlight}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}